import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';

export default function AgentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const isEdit = !!id;

  const [form, setForm] = useState({
    tls_code: '', full_name: '', email: '', phone: '', mobile: '',
    commission_rate: '', is_active: true, notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      supabase.from('agents').select('*').eq('id', id).single().then(({ data }) => {
        if (data) setForm({ ...data, commission_rate: data.commission_rate ?? '' });
      });
    } else {
      // Suggest the next available TLS-## code so staff aren't guessing and hitting the
      // unique constraint — codes so far are "TLS-01", "TLS-02" … a few non-numeric
      // outliers (e.g. "TLS-12-RISHAN") are ignored when computing the next number.
      supabase.from('agents').select('tls_code').then(({ data }) => {
        const maxNum = (data || []).reduce((max, a) => {
          const m = /^TLS-?(\d+)$/i.exec((a.tls_code || '').trim());
          return m ? Math.max(max, parseInt(m[1], 10)) : max;
        }, 0);
        setForm(f => ({ ...f, tls_code: `TLS-${String(maxNum + 1).padStart(2, '0')}` }));
      });
    }
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.tls_code || !form.full_name) return setError('TLS code and name are required');
    setSaving(true); setError('');
    try {
      const payload = {
        tls_code: form.tls_code.toUpperCase().trim(),
        full_name: form.full_name,
        email: form.email || null,
        phone: form.phone || null,
        mobile: form.mobile || null,
        commission_rate: parseFloat(form.commission_rate) || 0,
        is_active: form.is_active,
        notes: form.notes || null,
        updated_by: username,
        updated_at: new Date().toISOString(),
      };
      if (isEdit) {
        const { error: err } = await supabase.from('agents').update(payload).eq('id', id);
        if (err) throw err;
        navigate(`/agents/${id}`);
      } else {
        const { data, error: err } = await supabase.from('agents').insert({ ...payload, created_by: username }).select().single();
        if (err) throw err;
        navigate(`/agents/${data.id}`);
      }
    } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">{isEdit ? 'Edit Agent' : 'New Agent'}</div></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Agent'}</button>
        </div>
      </div>
      {error && <div style={{ color: 'var(--danger)', marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{error}</div>}
      <div className="card">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">TLS Code *</label>
            <input className="form-input" value={form.tls_code} onChange={e => set('tls_code', e.target.value)} placeholder="e.g. TLS001" style={{ fontFamily: 'monospace', fontWeight: 700, textTransform: 'uppercase' }} />
          </div>
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input className="form-input" value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Agent full name" />
          </div>
          <div className="form-group">
            <label className="form-label">Mobile</label>
            <input className="form-input" value={form.mobile} onChange={e => set('mobile', e.target.value)} placeholder="+94 77 000 0000" />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Default Commission Rate (%)</label>
            <input className="form-input" type="text" inputMode="decimal" value={form.commission_rate} onChange={e => set('commission_rate', e.target.value)} placeholder="0.00" />
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <div style={{ display: 'flex', gap: 10, paddingTop: 6 }}>
              {[true, false].map(v => (
                <label key={String(v)} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                  <input type="radio" checked={form.is_active === v} onChange={() => set('is_active', v)} />
                  {v ? 'Active' : 'Inactive'}
                </label>
              ))}
            </div>
          </div>
          <div className="form-group full-width">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
      </div>
    </div>
  );
}
